/**
 * FileTableRow Component
 * Reusable table row for file listings with actions
 */

import { forwardRef } from 'react';
import { Group, Text, Badge, Checkbox, ActionIcon } from '@/components/ui';
import {  IconEye, IconDownload, IconTrash, IconFolderSymlink } from '@tabler/icons-react';
import { FileTypeIcon } from './FileTypeIcon';
import { formatFileSize, formatTimeAgo } from '../../utils/formatting';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

export interface FileTableRowData {
  id: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  folderPath?: string;
  uploadedBy?: string;
  uploadedAt?: string;
  status?: string;
}

export interface FileTableRowProps {
  /** File data */
  file: FileTableRowData;
  /** Whether file is selected */
  selected?: boolean;
  /** Callback when selection changes */
  onSelect?: (fileId: string, selected: boolean) => void;
  /** Callback to preview file */
  onPreview?: (fileId: string) => void;
  /** Callback to download file */
  onDownload?: (fileId: string) => void;
  /** Callback to delete file */
  onDelete?: (fileId: string) => void;
  /** Callback to move file */
  onMove?: (fileId: string) => void;
  /** Show action buttons */
  showActions?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * Table row component for file listings
 *
 * @example
 * ```tsx
 * <table>
 *   <tbody>
 *     <FileTableRow
 *       file={{
 *         id: 'file-123',
 *         fileName: 'document.pdf',
 *         mimeType: 'application/pdf',
 *         fileSize: 1024000,
 *         uploadedBy: 'John Doe',
 *         uploadedAt: '2024-01-15T10:30:00Z',
 *         status: 'uploaded'
 *       }}
 *       selected={false}
 *       onSelect={(id, selected) => handleSelect(id, selected)}
 *       onPreview={(id) => handlePreview(id)}
 *       onDownload={(id) => handleDownload(id)}
 *       onDelete={(id) => handleDelete(id)}
 *     />
 *   </tbody>
 * </table>
 * ```
 */
export const FileTableRow = forwardRef<HTMLTableRowElement, FileTableRowProps>(
  (props, ref) => {
    const {
      file,
      selected = false,
      onSelect,
      onPreview,
      onDownload,
      onDelete,
      onMove,
      showActions = true,
      className,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onSelect?.(file.id, event.currentTarget.checked);
    };

    const handlePreview = (e: React.MouseEvent) => {
      e.stopPropagation();
      onPreview?.(file.id);
    };

    const handleDownload = (e: React.MouseEvent) => {
      e.stopPropagation();
      onDownload?.(file.id);
    };

    const handleDelete = (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.(file.id);
    };

    const handleMove = (e: React.MouseEvent) => {
      e.stopPropagation();
      onMove?.(file.id);
    };

    const getStatusColor = (): string => {
      switch (file.status) {
        case 'uploaded':
          return 'green';
        case 'pending':
          return 'yellow';
        case 'deleted':
          return 'red';
        default:
          return 'gray';
      }
    };

    return (
      <tr
        ref={ref}
        className={className}
        style={{
          backgroundColor: selected ? 'var(--mantine-color-blue-0)' : undefined,
          cursor: onPreview ? 'pointer' : 'default',
        }}
        onClick={onPreview ? () => onPreview(file.id) : undefined}
      >
        {/* Checkbox */}
        {onSelect && (
          <td style={{ width: 40 }}>
            <Checkbox
              checked={selected}
              onChange={handleCheckboxChange}
              aria-label={`Select ${file.fileName}`}
              onClick={(e) => e.stopPropagation()}
            />
          </td>
        )}

        {/* File Icon & Name */}
        <td>
          <Group gap="sm">
            <FileTypeIcon mimeType={file.mimeType} size="sm" />
            <Text size="sm" fw={500} lineClamp={1} style={{ maxWidth: 300, ...accessibilityStyles }}>
              {file.fileName}
            </Text>
          </Group>
        </td>

        {/* Type Badge */}
        <td>
          <Badge size="sm" variant="light">
            {file.mimeType.split('/')[0]}
          </Badge>
        </td>

        {/* Size */}
        <td>
          <Text size="sm" c="dimmed" style={accessibilityStyles}>
            {formatFileSize(file.fileSize)}
          </Text>
        </td>

        {/* Folder Path */}
        {file.folderPath !== undefined && (
          <td>
            <Text size="sm" c="dimmed" lineClamp={1} style={accessibilityStyles}>
              {file.folderPath || '/'}
            </Text>
          </td>
        )}

        {/* Uploaded By */}
        {file.uploadedBy !== undefined && (
          <td>
            <Text size="sm" c="dimmed" style={accessibilityStyles}>
              {file.uploadedBy}
            </Text>
          </td>
        )}

        {/* Uploaded At */}
        {file.uploadedAt !== undefined && (
          <td>
            <Text size="sm" c="dimmed" style={accessibilityStyles}>
              {formatTimeAgo(file.uploadedAt)}
            </Text>
          </td>
        )}

        {/* Status */}
        {file.status !== undefined && (
          <td>
            <Badge size="sm" variant="filled" color={getStatusColor()}>
              {file.status}
            </Badge>
          </td>
        )}

        {/* Actions */}
        {showActions && (
          <td>
            <Group gap={4} justify="flex-end">
              {onPreview && (
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={handlePreview}
                  aria-label="Preview file"
                >
                  <IconEye size={16} />
                </ActionIcon>
              )}
              {onDownload && (
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={handleDownload}
                  aria-label="Download file"
                >
                  <IconDownload size={16} />
                </ActionIcon>
              )}
              {onMove && (
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={handleMove}
                  aria-label="Move file"
                >
                  <IconFolderSymlink size={16} />
                </ActionIcon>
              )}
              {onDelete && (
                <ActionIcon
                  variant="subtle"
                  gradientVariant="danger"
                  size="sm"
                  onClick={handleDelete}
                  aria-label="Delete file"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              )}
            </Group>
          </td>
        )}
      </tr>
    );
  }
);

FileTableRow.displayName = 'FileTableRow';

export default FileTableRow;
